import { registrationAnalytics } from 'modules/registration/analytics';
import { endGroupProcessPayment } from 'modules/registration/actions/submit/groupRegistrations';
import { RegistrationStatus } from 'modules/registration/constants';
import { registrationService } from 'modules/registration/services';
import { preloaders } from 'modules/registration/utils';

import { fireToast, t } from 'utils';

const MAX_FAILED_POLLS = 5;
const FAILED_POLL_INTERVAL_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @description
 * Show the success screen once the backend has the registration.
 * Pay on site, free and card registrations are completed by a queued job, so pending is
 * expected here and still counts as success.
 *
 * Failed (status 3) only happens after a canceled or declined payment and can be temporary
 * (e.g. card declined, then paid with iDEAL), so it is re-checked a few times first.
 * paymentSucceeded: Stripe already confirmed the payment, so a failed status is never shown
 * as an error, the background check in registrationAnalytics reports it instead.
 *
 * Resolves only when the outcome is known, so callers can keep their loading state until then.
 */
export const checkRegistrationStatus = async (uuid: string, options: { paymentSucceeded?: boolean } = {}) => {
  if (!uuid) {
    return;
  }
  preloaders.submitInProgress.enable();
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });

  let status: number | nil = null;

  for (let poll = 0; poll <= MAX_FAILED_POLLS; poll++) {
    if (poll > 0) {
      await wait(FAILED_POLL_INTERVAL_MS);
    }

    const response = await registrationService.checkRegistrationStatus(uuid);

    if (!response.isOk) {
      preloaders.submitInProgress.disable();
      return;
    }

    status = response.data?.data?.status;

    if (status !== RegistrationStatus.canceled || options.paymentSucceeded) {
      break;
    }
  }

  if (status === RegistrationStatus.canceled && !options.paymentSucceeded) {
    preloaders.submitInProgress.disable();
    registrationAnalytics.paymentFailed({ type: 'registration_failed' });
    fireToast(t.staticAsString('registration.payment.declined'), 'error', t.staticAsString('registration.toast.validationError'));
    return;
  }

  registrationAnalytics.completed(uuid, status === RegistrationStatus.completed);
  endGroupProcessPayment();
  preloaders.submitInProgress.disable();
};
